import React, {
  useContext,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  Box,
  Chip,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { organizationsApi, usersApi } from "api";
import {
  useInfiniteScroll,
  useSelectedOrganization,
  useSnackbar,
  useTranslations,
  useUser,
  useBreakpoint,
} from "hooks";
import { getTranslation, OutletType } from "common";
import { useOutletContext } from "react-router-dom";
import { User } from "./UserItem";
import {
  Close,
  EditOutlined,
  ExitToApp,
  ForwardToInbox,
  MoreVertRounded,
  PersonRemove,
  StarBorder,
  Unsubscribe,
} from "@mui/icons-material";
import UserSelectedPanel from "./UserSelectedPanel";
import AvatarRem from "./AvatarRem";
import OrganizationResourceTable, {
  OrganizationResourceCards,
  OrganizationResourceColumn,
} from "pages/organizations/OrganizationResources";
import RemoveUserPopUp from "./dialogs/RemoveUserPopUp";
import { OrganizationsContext } from "contexts";
import { SupportedUserDataTypes } from "hooks/useUserDataKeys";

interface UsersPanelProps {
  onSelect: Function;
  organizationId: number;
  administrator: boolean;
}

type Ref =
  | {
    getUsers: () => void;
    getUserUntilPage: (userSelected: User) => Promise<void>;
    getInvitedUsers: () => void;
  }
  | undefined;

type InvitedUser = Partial<User> & {
  id: number;
  email: string;
  firstname?: string;
  lastname?: string;
  picture?: string | null;
};

type UserTableRow =
  | {
    status: "active";
    user: User;
  }
  | {
    status: "invited";
    user: InvitedUser;
  };

const INITIAL_PAGE = 1;
const PAGE_SIZE = 10;

const getName = (
  firstname?: string,
  lastname?: string,
  email?: string
): string => {
  return firstname && lastname ? `${lastname} ${firstname}` : email || "";
};

const renderLimitedChips = (items?: Array<{ id: number; name: string }>) => {
  if (!items?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }

  const visibleItems = items.slice(0, 2);
  const hiddenCount = items.length - visibleItems.length;

  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      {visibleItems.map((item) => (
        <Chip key={item.id} size="small" label={item.name} variant="outlined" />
      ))}
      {hiddenCount > 0 && (
        <Chip size="small" label={`+ ${hiddenCount}`} variant="outlined" />
      )}
    </Stack>
  );
};

const UsersPanel = forwardRef<Ref, UsersPanelProps>(
  ({ onSelect, organizationId, administrator }, ref) => {
    const [users, setUsers] = useState<Array<User>>([]);
    const [invited, setInvited] = useState<Array<InvitedUser>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [drawerUser, setDrawerUser] = useState<User | null>(null);
    const [userToRemove, setUserToRemove] = useState<User | null>(null);
    const [shouldRemoveUserPopup, setShouldRemoveUserPopup] = useState<boolean>(false);
    const translations = useTranslations();
    const {
      actions: { setSelectedOrganization },
    } = useContext(OrganizationsContext);
    const { secondFilter: userFilter } = useOutletContext<OutletType>();
    const [page, setPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const lastUserElementRef = useInfiniteScroll(setPage, loading, hasMore);
    const mount = useRef<boolean>(false);
    const { sendSnack } = useSnackbar();
    const selectedOrganization = useSelectedOrganization();
    const user = useUser();
    const isMobile = useBreakpoint("down", "sm");

    const getInvitedUsers = useCallback(async () => {
      try {
        const res = await organizationsApi.get(
          `${organizationId}/users/all?scope=notactivated`
        );
        setInvited(res.data.notactivated || []);
      } catch {
      } finally {
      }
    }, [organizationId]);

    const getFirstUsers = useCallback(async () => {
      setLoading(true);
      try {
        const queryPage = `?page=${INITIAL_PAGE}&pagesize=${PAGE_SIZE}`;
        const filter = `&name=${userFilter}`;
        const { data } = await organizationsApi.get(
          `${organizationId}/users/v2${queryPage}${filter}`
        );
        setHasMore(data.pagination.curr_page < data.pagination.total_pages);
        setUsers([...data.users]);
        setPage(INITIAL_PAGE);
      } catch {
      } finally {
        setLoading(false);
      }
    }, [organizationId, userFilter]);

    const getUserUntilPage = useCallback(
      async (userSelected: User) => {
        setLoading(true);
        try {
          const queryPage = `?page=${1}&pagesize=${PAGE_SIZE * page}`;
          const filter = `&name=${userFilter}`;
          const { data } = await organizationsApi.get(
            `${organizationId}/users/v2${queryPage}${filter}`
          );
          setHasMore(data.pagination.curr_page < data.pagination.total_pages);
          setUsers([...data.users]);
          if (userSelected) {
            const updatedSelected = (data.users as User[]).find(
              (_: User) => _.id === userSelected.id
            );
            if (updatedSelected) {
              onSelect({ ...updatedSelected });
              setDrawerUser({ ...updatedSelected });
            }
          }
        } catch {
        } finally {
          setLoading(false);
        }
      },
      [organizationId, onSelect, page, userFilter]
    );

    const getUsers = useCallback(
      async (query: string) => {
        if (page !== INITIAL_PAGE) {
          setLoading(true);
          try {
            const queryPage = `?page=${page}&pagesize=${PAGE_SIZE}`;
            const filter = `&name=${query}`;
            const { data } = await organizationsApi.get(
              `${organizationId}/users/v2${queryPage}${filter}`
            );
            setHasMore(
              Number(data.pagination.curr_page) <
              Number(data.pagination.total_pages)
            );
            setUsers((_users) => [..._users, ...data.users]);
          } catch {
          } finally {
            setLoading(false);
          }
        }
      },
      [organizationId, page]
    );

    useImperativeHandle(
      ref,
      () => ({
        getUserUntilPage,
        getUsers: getFirstUsers,
        getInvitedUsers,
      }),
      [getFirstUsers, getUserUntilPage, getInvitedUsers]
    );

    useEffect(() => {
      getInvitedUsers();
    }, [getInvitedUsers]);

    useEffect(() => {
      getUsers(userFilter);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getUsers]);

    useEffect(() => {
      if (organizationId && mount.current) {
        getFirstUsers();
        getInvitedUsers();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId, userFilter]);

    useEffect(() => {
      if (!mount.current) {
        mount.current = true;
        getFirstUsers();
      }
    }, [getFirstUsers]);

    const onDeleteInvitedUser = async (userId: number) => {
      try {
        const { data } = await organizationsApi.delete(
          `${selectedOrganization?.id}/users/${userId}`
        );
        sendSnack({ message: data.responsemessage, type: "success" });
        getInvitedUsers();
      } catch {
      } finally {
        setInviteMenuOpen(null);
      }
    };

    const handleRemoveUser = async (userId: number) => {
      try {
        const { data } = await organizationsApi.delete(
          `/${organizationId}/users/${userId}`
        );
        sendSnack({
          message: `${data.responsemessage}`,
          type: "success",
        });
        if (user?.userId === userId) {
          setSelectedOrganization(null);
        } else {
          getFirstUsers();
          getInvitedUsers();
        }
      } catch {
      } finally {
        setShouldRemoveUserPopup(false);
        setUserToRemove(null);
      }
    };

    const onReinviteUser = async (id: number, email: string) => {
      try {
        await usersApi.post(
          `${user?.userId}/organizations/${selectedOrganization?.id}/resendInvite/${id}`,
          { email: [email] }
        );
        sendSnack({
          message: getTranslation(
            translations,
            "generic.user_reinvite_success_wait_for_response.caption"
          ),
          type: "success",
        });
        getInvitedUsers();
      } catch {
      } finally {
        setInviteMenuOpen(null);
      }
    };

    const [inviteMenuOpen, setInviteMenuOpen] = useState<{
      anchorEl: HTMLElement | null;
      userId: number | null;
      email: string | null;
    } | null>(null);

    const openUserDrawer = (rowUser: User) => {
      if (!administrator) return;
      onSelect(rowUser);
      setDrawerUser(rowUser);
    };

    const closeUserDrawer = () => {
      setDrawerUser(null);
      onSelect(null);
    };

    const rows: UserTableRow[] = [
      ...users.map((tableUser) => ({ status: "active" as const, user: tableUser })),
      ...invited
        .filter((invitedUser) =>
          getName(invitedUser.firstname, invitedUser.lastname, invitedUser.email)
            .toLowerCase()
            .includes(userFilter.toLowerCase())
        )
        .map((invitedUser) => ({ status: "invited" as const, user: invitedUser })),
    ];

    const columns: OrganizationResourceColumn<UserTableRow>[] = [
      {
        id: "user",
        label: getTranslation(translations, "generic.user"),
        sortType: SupportedUserDataTypes.string,
        sortValue: (row) =>
          getName(row.user.firstname, row.user.lastname, row.user.email),
        render: (row) => {
          const tableUser = row.user;
          const isActive = row.status === "active";
          const activeUser = isActive ? (tableUser as User) : null;
          const rowName = getName(
            tableUser.firstname,
            tableUser.lastname,
            tableUser.email
          );

          return (
            <Stack direction="row" alignItems="center" spacing={2}>
              <AvatarRem
                icon={isActive ? `/users/${tableUser.id}/picture` : ""}
                imgProps={{ referrerPolicy: "no-referrer" }}
              >
                {rowName.slice(0, 1).toUpperCase()}
              </AvatarRem>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography fontWeight={700} noWrap>
                    {rowName}
                  </Typography>
                  {activeUser?.administrator && (
                    <Chip
                      size="small"
                      icon={<StarBorder sx={{ fontSize: 14 }} />}
                      label="ADMIN"
                      color="secondary"
                      variant="outlined"
                      sx={{ height: 22, fontWeight: 700 }}
                    />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {tableUser.email}
                </Typography>
              </Box>
            </Stack>
          );
        },
      },
      {
        id: "groups",
        label: getTranslation(translations, "groups.name"),
        sortType: SupportedUserDataTypes.string,
        sortValue: (row) =>
          row.status === "active"
            ? ((row.user as User).groups || []).map((group) => group.name).join(", ")
            : "",
        render: (row) =>
          renderLimitedChips(
            row.status === "active" ? (row.user as User).groups : undefined
          ),
      },
      {
        id: "roles",
        label: getTranslation(translations, "roles.name"),
        sortType: SupportedUserDataTypes.string,
        sortValue: (row) =>
          row.status === "active"
            ? ((row.user as User).roles || []).map((role) => role.name).join(", ")
            : "",
        render: (row) =>
          renderLimitedChips(
            row.status === "active" ? (row.user as User).roles : undefined
          ),
      },
      {
        id: "status",
        label: getTranslation(translations, "generic.status"),
        render: (row) => (
          <Chip
            size="small"
            label={row.status === "active" ? getTranslation(translations, "generic.active") : getTranslation(translations, "generic.invited")}
            color={row.status === "active" ? "success" : "warning"}
            sx={{ fontWeight: 700 }}
          />
        ),
      },
      {
        id: "actions",
        label: getTranslation(translations, "generic.actions"),
        align: "right",
        render: (row) => {
          const tableUser = row.user;
          if (row.status === "active") {
            const activeUser = tableUser as User;
            const isLoggedUser = user?.userId === activeUser.id;
            return administrator ? (
              <Stack direction="row" justifyContent="flex-end" spacing={1}>
                <Tooltip title={getTranslation(translations, "users.manage_user")}>
                  <IconButton
                    id={`user-actions-${tableUser.id}`}
                    size="small"
                    color="secondary"
                    onClick={(event) => {
                      event.stopPropagation();
                      openUserDrawer(activeUser);
                    }}
                  >
                    <EditOutlined />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={
                    isLoggedUser
                      ? getTranslation(translations, "generic.exit_organization.caption")
                      : getTranslation(translations, "generic.remove")
                  }
                >
                  <IconButton
                    id={`user-delete-${tableUser.id}`}
                    size="small"
                    color="error"
                    onClick={(event) => {
                      event.stopPropagation();
                      setUserToRemove(activeUser);
                      setShouldRemoveUserPopup(true);
                    }}
                  >
                    {isLoggedUser ? <ExitToApp /> : <PersonRemove />}
                  </IconButton>
                </Tooltip>
              </Stack>
            ) : null;
          }

          return (
            <IconButton
              id={`invited-users-btn-${tableUser.id}`}
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                setInviteMenuOpen({
                  anchorEl: event.currentTarget,
                  userId: tableUser.id,
                  email: tableUser.email,
                });
              }}
            >
              <MoreVertRounded />
            </IconButton>
          );
        },
      },
    ];

    return (
      <React.Fragment>
        <Stack height="100%" overflow="hidden" flex={1}>
          {isMobile ? (
            <OrganizationResourceCards
              id="users-list"
              rows={rows}
              columns={columns}
              loading={loading}
              getRowKey={(row) => `${row.status}-${row.user.id}`}
              getRowId={(row) =>
                row.status === "active"
                  ? `user-${row.user.id}`
                  : `invited-${row.user.id}`
              }
              canHover={(row) => administrator && row.status === "active"}
              onRowClick={(row) => {
                if (row.status === "active") {
                  openUserDrawer(row.user as User);
                }
              }}
              getLastRowRef={(row, index) =>
                row.status === "active" && users.length === index + 1
                  ? lastUserElementRef
                  : undefined
              }
            />
          ) : (
            <OrganizationResourceTable
              id="users-list"
              rows={rows}
              columns={columns}
              loading={loading}
              getRowKey={(row) => `${row.status}-${row.user.id}`}
              getRowId={(row) =>
                row.status === "active"
                  ? `user-${row.user.id}`
                  : `invited-${row.user.id}`
              }
              canHover={(row) => administrator && row.status === "active"}
              onRowClick={(row) => {
                if (row.status === "active") {
                  openUserDrawer(row.user as User);
                }
              }}
              getLastRowRef={(row, index) =>
                row.status === "active" && users.length === index + 1
                  ? lastUserElementRef
                  : undefined
              }
            />
          )}
        </Stack>
        <Menu
          open={Boolean(inviteMenuOpen)}
          anchorEl={inviteMenuOpen?.anchorEl}
          onClose={() => setInviteMenuOpen(null)}
        >
          <MenuItem
            id={`resend-invitation-${inviteMenuOpen?.userId}`}
            onClick={(event) => {
              event.stopPropagation();
              onReinviteUser(inviteMenuOpen?.userId!, inviteMenuOpen?.email!);
            }}
          >
            <ListItemIcon>
              <ForwardToInbox fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {getTranslation(
                translations,
                "generic.resend_invitation.caption"
              )}
            </ListItemText>
          </MenuItem>
          <MenuItem
            id={`delete-invitation-${inviteMenuOpen?.userId}`}
            onClick={(event) => {
              event.stopPropagation();
              onDeleteInvitedUser(inviteMenuOpen?.userId!);
            }}
          >
            <ListItemIcon>
              <Unsubscribe fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {getTranslation(
                translations,
                "generic.delete_invitation.caption"
              )}
            </ListItemText>
          </MenuItem>
        </Menu>
        <Drawer
          anchor="right"
          open={Boolean(drawerUser)}
          onClose={closeUserDrawer}
          PaperProps={{ sx: { width: { xs: "100%", sm: 480 } } }}
        >
          <Stack height="100%" overflow="hidden">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}
            >
              <Typography variant="h6" fontWeight={800}>
                {getTranslation(translations, "pm.startprocess.step2")}
              </Typography>
              <IconButton onClick={closeUserDrawer}>
                <Close />
              </IconButton>
            </Stack>
            <Box sx={{ p: 3, overflow: "auto", flex: 1 }}>
              {drawerUser && (
                <UserSelectedPanel
                  inMobile
                  userLoggedIsAdmin={administrator}
                  refresh={() => {
                    getFirstUsers();
                  }}
                  getUserUntilPage={getUserUntilPage}
                  userSelected={drawerUser}
                  organizationId={organizationId}
                />
              )}
            </Box>
          </Stack>
        </Drawer>
        {shouldRemoveUserPopup && user && (
          <RemoveUserPopUp
            idLoggedUser={user.userId}
            idUser={userToRemove?.id}
            handleRemoveUser={handleRemoveUser}
            shouldRemoveUserPopup={shouldRemoveUserPopup}
            setShouldRemoveUserPopup={setShouldRemoveUserPopup}
          />
        )}
      </React.Fragment>
    );
  }
);

export default UsersPanel;
